import React, { useEffect } from 'react';
import { Badge, Stack, Button } from 'react-bootstrap';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { postDigiclueplus } from 'api';
import { getDigiclueplusCouponsUnseens } from 'api/digiclueplus';
import { openWindow } from 'lib';
import HomeOn from 'assets/image/icons/home_on.svg';
import HomeOff from 'assets/image/icons/home_off.svg';
import CouponOn from 'assets/image/icons/coupon_on.svg';
import CouponOff from 'assets/image/icons/coupon_off.svg';
import StoreOn from 'assets/image/icons/store_on.svg';
import StoreOff from 'assets/image/icons/store_off.svg';
import NewsOn from 'assets/image/icons/news_on.svg';
import NewsOff from 'assets/image/icons/news_off.svg';
import UserOn from 'assets/image/icons/user_on.svg';
import UserOff from 'assets/image/icons/user_off.svg';
import { getCurrentNotificationsUnread } from 'api';
import { useStateMachine } from 'little-state-machine';
import {
  updateNotificationsUnreadCount,
  updateCouponsUnreadCount
} from 'lib/stateActions';
import dayjs from 'dayjs';

const Footer: React.FC = () => {
  const { pathname } = useLocation();
  const footer = {
    home: {
      class: '',
      icon: HomeOff
    },
    coupon: {
      class: '',
      icon: CouponOff
    },
    store: {
      class: '',
      icon: StoreOff
    },
    news: {
      class: '',
      icon: NewsOff
    },
    user: {
      class: '',
      icon: UserOff
    }
  };
  if (matchPath('/', pathname) || matchPath('/history/*', pathname)) {
    footer.home.class = 'active';
    footer.home.icon = HomeOn;
  }
  /**
   * クーポンページを削除したら、この処理も削除する
   */
  if (matchPath('/coupon/*', pathname)) {
    footer.coupon.class = 'active';
    footer.coupon.icon = CouponOn;
  }
  if (matchPath('/mystore/*', pathname)) {
    footer.store.class = 'active';
    footer.store.icon = StoreOn;
  }
  if (matchPath('/news/*', pathname)) {
    footer.news.class = 'active';
    footer.news.icon = NewsOn;
  }
  if (matchPath('/mypage/*', pathname)) {
    footer.user.class = 'active';
    footer.user.icon = UserOn;
  }

  const {
    actions,
    state: {
      notificationsUnreadCount, // 未読のお知らせ数
      notificationsUnreadCountUpdatedAt, // 未読のお知らせ数 の更新時刻
      couponsUnreadCount, // 未読のクーポン数
      couponsUnreadCountUpdatedAt // 未読のクーポン数 の更新時刻
    }
  } = useStateMachine({
    updateNotificationsUnreadCount,
    updateCouponsUnreadCount
  });

  const [{ loading }, onClickDigiclueplusCoupon] = useAsyncFn(async () => {
    const res = await postDigiclueplus('simple_coupons');
    if (!res || res.status === 'failed')
      return window.location.replace(process.env.PUBLIC_URL + '/error/500');
    openWindow(res.data.service_url, false);
  }, []);

  // 未読のお知らせ数取得
  useEffect(() => {
    // 更新時刻から 30秒 以内は再取得を行わない
    // TODO: テスタビリティ改善のため、時間計算処理をlib層に切り出すことを検討
    // 現在day.jsがコンポーネント内で直接使用されており、テスト時のモックが複雑になっている
    if (
      notificationsUnreadCountUpdatedAt &&
      dayjs().diff(dayjs(notificationsUnreadCountUpdatedAt), 'second') < 30
    )
      return;

    getCurrentNotificationsUnread().then((res) => {
      if (!res || res.status === 'failed') return;
      actions.updateNotificationsUnreadCount(res.data.unread_count);
    });
  }, [notificationsUnreadCountUpdatedAt]);

  // 未読のクーポン数取得
  useEffect(() => {
    // 更新時刻から 30秒 以内は再取得を行わない
    if (
      couponsUnreadCountUpdatedAt &&
      dayjs().diff(dayjs(couponsUnreadCountUpdatedAt), 'second') < 30
    )
      return;

    getDigiclueplusCouponsUnseens().then((res) => {
      if (!res || res.status === 'failed') return;
      actions.updateCouponsUnreadCount(res.data.unread_count);
    });
  }, [couponsUnreadCountUpdatedAt]);

  return (
    <Stack
      as="ul"
      direction="horizontal"
      className="list-unstyled justify-content-around bg-white m-0 footer"
    >
      <li className="text-center pb-2 pt-3">
        <Link to="/" className={footer.home.class}>
          <img src={footer.home.icon} alt="ホーム" className="mb-2" />
        </Link>
      </li>
      <li className="text-center pb-2 pt-3">
        <Button
          className={`btn p-0 border-0 bg-transparent position-relative ${footer.coupon.class}`}
          disabled={loading}
          onClick={onClickDigiclueplusCoupon}
        >
          <img src={footer.coupon.icon} alt="クーポン" className="mb-2" />
          {/* 未読バッジ */}
          {couponsUnreadCount > 0 && (
            <Badge
              pill
              bg="danger"
              className="position-absolute top-0 end-0 d-block p-1"
            />
          )}
        </Button>
      </li>
      <li className="text-center pb-2 pt-3">
        <Link to="/mystore" className={footer.store.class}>
          <img src={footer.store.icon} alt="マイストア" className="mb-2" />
        </Link>
      </li>
      <li className="text-center pb-2 pt-3">
        <Link
          to="/news"
          className={`d-block position-relative ${footer.news.class}`}
        >
          <img src={footer.news.icon} alt="お知らせ" className="mb-2" />
          {/* 未読バッジ */}
          {notificationsUnreadCount > 0 && (
            <Badge
              pill
              bg="danger"
              className="position-absolute top-0 end-0 d-block p-1"
            />
          )}
        </Link>
      </li>
      <li className="text-center pb-2 pt-3">
        <Link to="/mypage" className={footer.user.class}>
          <img src={footer.user.icon} alt="マイページ" className="mb-2" />
        </Link>
      </li>
    </Stack>
  );
};
export default Footer;
